import {
  FileText,
  GitBranch,
  CheckCircle2,
  ShieldCheck,
  KeyRound,
  Database,
  Wrench,
  Globe2,
  Boxes,
  AlertTriangle,
  ScrollText,
  Archive,
  ListChecks,
} from 'lucide-react';
import type {
  ListDaemonPermissionGrantsResponse,
  ListDaemonRunDecisionsResponse,
  ListDaemonRunExecutionEnvelopesResponse,
  ListDaemonRunReportsResponse,
} from '../../../generated/api/types.gen.js';

export const STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'danger' | 'neutral' | 'info'> = {
  completed: 'success',
  running: 'info',
  pending: 'neutral',
  failed: 'danger',
  // Did not run: held for a person's approval, or stopped by a limit. Nothing went wrong.
  skipped: 'warning',
  denied: 'danger',
  validated: 'info',
};

export const RISK_VARIANTS: Record<string, 'success' | 'warning' | 'danger' | 'neutral' | 'info'> = {
  low: 'success',
  medium: 'warning',
  high: 'danger',
};

// Human labels for envelope actions, including the resource-access envelopes
// (repo checkout/snapshot read, secret access, network egress) so each reads
// clearly alongside container/tool/MCP command envelopes.
export const ACTION_LABEL: Record<string, string> = {
  container_exec: 'Container exec',
  tool_call: 'Tool call',
  mcp_call: 'MCP call',
  repo_checkout: 'Repo checkout',
  repo_snapshot_read: 'Repo snapshot read',
  secret_access: 'Secret access',
  network_egress: 'Network egress',
};

export const AUDIT_TABS = [
  { id: 'overview', label: 'Overview', icon: Database },
  { id: 'steps', label: 'Steps', icon: ListChecks },
  { id: 'tools', label: 'Tools', icon: Wrench },
  { id: 'resourceAccess', label: 'Resource Access', icon: KeyRound },
  { id: 'sources', label: 'Sources', icon: Globe2 },
  { id: 'fragments', label: 'Fragments', icon: FileText },
  { id: 'integrity', label: 'Integrity', icon: ShieldCheck },
  { id: 'candidates', label: 'Candidates', icon: Boxes },
  { id: 'reviewQueue', label: 'Review Queue', icon: ListChecks },
  { id: 'findings', label: 'Findings', icon: AlertTriangle },
  { id: 'versions', label: 'Candidate Versions', icon: GitBranch },
  { id: 'decisions', label: 'Decisions', icon: CheckCircle2 },
  { id: 'reports', label: 'Reports', icon: ScrollText },
  { id: 'artifacts', label: 'Artifacts', icon: Archive },
] as const;

export type AuditTabId = (typeof AUDIT_TABS)[number]['id'];

export const AUDIT_EMPTY_LABELS: Record<AuditTabId, string> = {
  overview: 'No overview recorded for this run.',
  steps: 'No workflow steps recorded for this run.',
  tools: 'No execution envelopes recorded for this run.',
  resourceAccess: 'No resource-access envelopes recorded for this run.',
  sources: 'No catalog sources recorded for this run.',
  fragments: 'No source fragments recorded for this run.',
  integrity: 'No catalog integrity checks recorded for this run.',
  candidates: 'No catalog candidates recorded for this run.',
  reviewQueue: 'No review queue rows recorded for this run.',
  findings: 'No daemon findings recorded for this run.',
  versions: "No promoted catalog versions recorded for this run's candidates.",
  decisions: 'No decisions recorded for this run.',
  reports: 'No reports recorded for this run.',
  artifacts: 'No artifacts recorded for this run.',
};

/**
 * Element types for the four run sub-lists, derived from the generated response types rather
 * than restated — a field renamed in the API breaks this file instead of silently becoming
 * `undefined` at runtime (QG-CLI-PAGES-006).
 */
export type RunReport = NonNullable<ListDaemonRunReportsResponse['data']>[number];
export type RunDecision = NonNullable<ListDaemonRunDecisionsResponse['data']>[number];
export type RunEnvelope = NonNullable<ListDaemonRunExecutionEnvelopesResponse['data']>[number];
export type RunGrant = NonNullable<ListDaemonPermissionGrantsResponse['data']>[number];

/**
 * These endpoints are mounted at two paths (daemon + employee) and their responses arrive either
 * bare, wrapped once in `{ data }`, or wrapped twice. This is the ONE place that shape guessing
 * happens, so the cast lives here and every caller downstream is typed.
 */
export function unwrapListResponse<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (!value || typeof value !== 'object') return [];
  const data = (value as { data?: unknown }).data;
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object') {
    const nested = (data as { data?: unknown }).data;
    if (Array.isArray(nested)) return nested as T[];
  }
  return [];
}

export function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

export function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/**
 * Render helpers for genuinely open payload fields — `metrics`, and the envelope/grant rows the
 * API still declares as free-form records. QG-CLI-PAGES-006 replaced the blanket `any` on these
 * components with the generated types; where the CONTRACT itself says "unknown", the value has
 * to be narrowed at the point of display rather than asserted away.
 */
export function asText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

/** A numeric metric for display; anything non-numeric reads as the fallback. */
export function asCount(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** A record key that may be absent or non-string in an open payload. */
export function asKey(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

export function formatCommand(value: unknown): string {
  if (Array.isArray(value)) return value.map(String).join(' ');
  return typeof value === 'string' ? value : 'n/a';
}
